import {formatDate} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {adbCategoryAxis, adbChartTheme, adbGrid, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {adbUsdCompact, adbUsdFull} from '@features/admin-panel/helpers/admin-dashboard-format';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillCell, AdminDashboardDrillView} from '@features/admin-panel/models/admin-dashboard-drill.models';
import {
  AdminDashboardLicenceExpiryDrill,
  AdminDashboardLicenceExpiryDrillRow
} from '@features/admin-panel/models/admin-dashboard-licences.models';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {
  AdminDashboardBucket,
  AdminDashboardLicenceExpiry,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const expiryTimelineCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** The card's title, on screen and in the PDF report (OCA-E-159 DetailSections:292). */
const TITLE = 'Expiry timeline';
/** The description before the first response and in the not-applicable scope. */
const NO_HEADLINE = 'Licence value ending in the next 90 days.';
/** The drill's health pill per tier: label and tone (OCA-E-159 attention.ts TIER_LABEL / TIER_BADGE). */
const TIER_PILLS: {[tier: string]: {label: string; tone: 'red' | 'amber' | 'blue' | 'green'}} = {
  critical: {label: 'Critical', tone: 'red'},
  warning: {label: 'Warning', tone: 'amber'},
  watch: {label: 'Watch', tone: 'blue'},
  healthy: {label: 'Healthy', tone: 'green'}
};

/**
 * Expiry timeline: a bar per window (0-30, 31-60, 61-90 days) of the USD licence value expiring in it, labelled
 * "$12.0k · 3", with the headline (value and licence count over all windows) in the description. Reloads whenever the
 * dashboard's query params change. Licences exist only in Client Manager, so when the server answers NotApplicable
 * (the NonClients scope) no chart is drawn: the shared empty state says "No Client Manager record" at the chart's
 * size. The chart is created only once there is data to draw. A bar click opens the drill of that bucket's
 * contracts (ITP-E-160-02); Evidence there opens the why panel through `companySelected`.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-expiry-timeline',
  templateUrl: './admin-dashboard-expiry-timeline.component.html',
  styleUrls: ['./admin-dashboard-expiry-timeline.component.scss']
})
export class AdminDashboardExpiryTimelineComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** Evidence was asked for a company in the drill: the parent opens its why panel. */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = TITLE;
  readonly failedTitle = 'Couldn\'t load the expiry timeline';
  buckets: AdminDashboardBucket[] = [];
  /** The server's NotApplicable: there is nothing to chart in this scope. */
  notApplicable = false;
  /** False until the first response, so a not-applicable scope never creates a chart. */
  loaded = false;
  /** The last load failed: the card says so instead of drawing an empty chart. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(
    private dataService: AdminDashboardDataService,
    private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>,
    private drill: AdminDashboardDrillService
  ) {}

  /**
   * The description under the title, from the bucket sums (OCA-E-159 DetailSections:292-296). Each bucket sums
   * TotalLicensePrice per licence, so the mockup's "ARR across N contracts" reads "licence value across N licences".
   */
  get headline(): string {
    if (!this.loaded || this.notApplicable || this.failed) {
      return NO_HEADLINE;
    }
    const total = this.buckets.reduce((sum, bucket) => sum + (bucket?.value ?? 0), 0);
    const count = this.buckets.reduce((sum, bucket) => sum + (bucket?.count ?? 0), 0);
    if (!count) {
      return 'No licences end in the next 90 days.';
    }
    return `${adbUsdCompact(total)} of licence value across ${countOf(count, 'licence')} ends in the next 90 days.`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('licences-revenue', this.host.nativeElement, this);
  }

  /**
   * The chart container exists only while there is something to draw: the chart is created when it appears and
   * disposed when it goes.
   */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = expiryTimelineCharts.init(this.chartElement);
      this.chart.on('click', (event: any) => this.openBucket(this.buckets[event?.dataIndex]));
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-10). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: TITLE, text: this.failedTitle}];
    }
    if (this.notApplicable) {
      return [{kind: 'empty', title: TITLE, subtitle: this.headline, text: 'No Client Manager record'}];
    }
    return [{
      kind: 'chart',
      title: TITLE,
      subtitle: this.headline,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    }];
  }

  /** "$12,000 · 3 licences" */
  tooltipText(bucket: AdminDashboardBucket): string {
    return `${adbUsdFull(bucket?.value ?? 0)} · ${countOf(bucket?.count ?? 0, 'licence')}`;
  }

  /** The label on top of a bar: "$12.0k · 3" (OCA-E-159 charts.ts:337-343). */
  barLabel(bucket: AdminDashboardBucket): string {
    return `${adbUsdCompact(bucket?.value ?? 0)} · ${bucket?.count ?? 0}`;
  }

  /**
   * A bar was clicked (OCA-E-159 DetailSections:252-266): the drill lists that bucket's contracts, loaded with the
   * bucket's label exactly as the server sent it (ITP-E-160-01). Evidence there emits `companySelected`.
   */
  openBucket(bucket: AdminDashboardBucket | undefined): void {
    if (!bucket || !this.params) {
      return;
    }
    const params = this.params;
    this.drill.open({
      title: `Contracts ending in ${bucket.label}`,
      columns: [{header: 'Ends', align: 'end'}, {header: 'ARR', align: 'end'}, {header: 'Health', align: 'end'}],
      load: () => this.dataService.getWithExtra<AdminDashboardLicenceExpiryDrill>(
        'AdminDashboardLicenceExpiryDrillQuery', params, {Bucket: bucket.label}
      ).pipe(map(response => this.drillView(response)))
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** The drill's view of a response: "$4,500 ARR across 3 accounts.", one row per contract (Ends, ARR, Health). */
  drillView(response: AdminDashboardLicenceExpiryDrill): AdminDashboardDrillView {
    const rows = Array.isArray(response?.Rows) ? response.Rows : [];
    return {
      subtitle: `${adbUsdFull(response?.Revenue ?? 0)} ARR across ${countOf(response?.Accounts ?? 0, 'account')}.`,
      rows: rows.map(row => adbDrillRowView(row, [
        {text: formatDate(row.ends, 'd MMM', 'en-US', 'UTC')},
        {text: adbUsdFull(row.arr)},
        this.healthCell(row)
      ])),
      total: response?.Total ?? rows.length
    };
  }

  /** "Critical 34" as a red pill (amber Warning, blue Watch, green Healthy); "–" before the company has an insight. */
  healthCell(row: AdminDashboardLicenceExpiryDrillRow): AdminDashboardDrillCell {
    const pill = TIER_PILLS[row?.tier ?? ''];
    if (!pill || row.healthScore === null || row.healthScore === undefined) {
      return {text: '–'};
    }
    return {text: `${pill.label} ${row.healthScore}`, tone: pill.tone, pill: true};
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getLicenceExpiry(this.params).subscribe({
      next: expiry => this.show(expiry, false),
      error: () => this.show(null, true)
    });
  }

  /** No Client Manager record (the NonClients scope) has nothing to show: it reports empty, as does no bucket at all. */
  private show(expiry: AdminDashboardLicenceExpiry | null, failed: boolean): void {
    this.buckets = expiry?.Buckets ?? [];
    this.notApplicable = expiry?.NotApplicable === true;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.notApplicable || !this.buckets.length ? 'empty' : 'content');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  /** OCA-E-159 charts.ts:315-345: blue-2 bars with rounded tops and value labels, themed axes, a compact $ axis. */
  private render(): void {
    if (!this.chart || this.notApplicable || this.failed) {
      return;
    }
    const buckets = this.buckets;
    const t = adbChartTheme(this.host.nativeElement);
    const option = {
      grid: adbGrid({top: 34}),
      xAxis: adbCategoryAxis(t, buckets.map(b => b.label)),
      yAxis: adbValueAxis(t, (value: number) => adbUsdCompact(value)),
      series: [{
        type: 'bar',
        data: buckets.map(b => b.value),
        barMaxWidth: 56,
        itemStyle: {color: t.blue2, borderRadius: [4, 4, 0, 0]},
        label: {
          show: true,
          position: 'top',
          color: t.ink,
          fontSize: 11,
          formatter: (params: {dataIndex: number}) => this.barLabel(buckets[params?.dataIndex])
        }
      }],
      tooltip: adbTooltip(t, {
        axisPointer: {type: 'shadow'},
        formatter: (points: any) => {
          const point = Array.isArray(points) ? points[0] : points;
          return `<b>Contracts ending in ${point?.name}</b><br/>${this.tooltipText(buckets[point?.dataIndex])}`
            + `<br/><span style="color:${t.ink2}">Click to list them</span>`;
        }
      })
    };
    this.chart.setOption(option as unknown as echarts.EChartsOption, true);
  }
}
